<template>
  <div class="fsm-root">
    <GmapMap
      ref="gmap"
      class="fsm-map"
      :center="mapCenter"
      :zoom="mapZoom"
      :options="mapOptions"
      @click="onMapClick"
    >
      <GmapMarker
        v-for="m in mapMarkers"
        :key="m.work.id"
        :position="m.position"
        :icon="savedMarkerIcon"
        :label="markerLabel(m.letterIndex)"
        :clickable="true"
        :zIndex="1"
      />
      <GmapMarker
        v-if="pending"
        :position="pending"
        :icon="pendingMarkerIcon"
        :zIndex="10"
      >
        <GmapInfoWindow
          :opened="!!pending"
          :options="infoWindowOptions"
          @closeclick="closePending"
        >
          <div class="fsm-popup" @click.stop>
            <div class="fsm-popup-body">
              <div class="fsm-field">
                <label class="fsm-label" for="fsm-draft-name">{{ $t('FieldSurveyForm.fieldName') }}</label>
                <input
                  id="fsm-draft-name"
                  v-model.trim="draftName"
                  type="text"
                  class="fsm-input"
                  :disabled="saving"
                  :placeholder="$t('FieldSurveyForm.fieldName')"
                  autocomplete="off"
                >
              </div>
              <div class="fsm-field">
                <span class="fsm-label">{{ $t('FieldSurveyForm.fieldUser') }}</span>
                <survey-work-technician-select
                  v-model="draftUserId"
                  class="fsm-tech-select"
                  :items="technicianItems"
                  :loading="technicianOptionsLoading"
                  :disabled="saving"
                  :compact="false"
                  :field-survey-id="fieldSurveyId"
                  @user-created="$emit('technician-user-created', $event)"
                />
              </div>
              <div class="fsm-field fsm-field--upload">
                <input
                  ref="chartFileInput"
                  type="file"
                  class="fsm-file-input"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                  :disabled="saving"
                  @change="onChartFileSelected"
                >
                <button
                  type="button"
                  class="fsm-upload-zone"
                  :disabled="saving"
                  :aria-label="$t('FieldSurveyForm.chartUploadButtonLabel')"
                  @click="openChartFilePicker"
                >
                  <v-icon class="fsm-upload-icon" size="18" color="#90a4ae">mdi-tray-arrow-up</v-icon>
                  <span class="fsm-upload-label">{{ $t('FieldSurveyForm.chartUploadButtonLabel') }}</span>
                </button>
                <p v-if="pendingChartFile" class="fsm-file-hint">{{ pendingChartFile.name }}</p>
              </div>
            </div>
            <div class="fsm-popup-footer">
              <v-btn
                block
                depressed
                rounded
                :color="saveBtnColor"
                class="fsm-save-btn text-none"
                :loading="saving"
                :disabled="!canSubmitPopup || saving"
                @click="submitPopup"
              >
                {{ $t('FieldSurveyForm.save') }}
              </v-btn>
            </div>
          </div>
        </GmapInfoWindow>
      </GmapMarker>
    </GmapMap>

    <div class="fsm-search-bar">
      <GmapAutocomplete
        :placeholder="$t('FieldSurveyForm.mapSearchPlaceholder')"
        class="fsm-search-input"
        :disabled="disabled"
        @place_changed="onPlaceChanged"
      />
      <v-icon class="fsm-search-icon" size="20" color="#757575">mdi-magnify</v-icon>
    </div>
  </div>
</template>

<script>
import SurveyWorkTechnicianSelect from './SurveyWorkTechnicianSelect.vue'
import { PONCE_TECHNICIAN_SELECT_VALUE } from '@/lib/surveyWorkTechnicians'

const ARGENTINA_CENTER = { lat: -34.6, lng: -64.5 }
const ARGENTINA_ZOOM = 4
const USER_LOCATION_ZOOM = 10
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false,
  maximumAge: 300000,
  timeout: 10000
}
const DEFAULT_MARKER_ICON = require('@/assets/icons/fieldWorkMarker.png')
const PENDING_MARKER_ICON = 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'

function parseSurveyWorkCoordinates (str) {
  if (!str || typeof str !== 'string') return null
  const parts = str.split(',').map((s) => parseFloat(String(s).trim()))
  if (parts.length < 2 || !Number.isFinite(parts[0]) || !Number.isFinite(parts[1])) return null
  return { lat: parts[0], lng: parts[1] }
}

function formatSurveyWorkCoordinates (lat, lng) {
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`
}

export default {
  name: 'FieldSurveyMap',
  components: { SurveyWorkTechnicianSelect },
  props: {
    surveyWorks: {
      type: Array,
      required: true
    },
    devicesQtyNumber: {
      type: Number,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    technicianItems: {
      type: Array,
      default: () => []
    },
    technicianOptionsLoading: {
      type: Boolean,
      default: false
    },
    fieldSurveyId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      mapCenter: { ...ARGENTINA_CENTER },
      mapZoom: ARGENTINA_ZOOM,
      mapOptions: {
        mapTypeId: 'hybrid',
        zoomControl: true,
        panControl: false,
        rotateControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        cameraControl: false
      },
      infoWindowOptions: {
        maxWidth: 300
      },
      pending: null,
      draftName: '',
      draftUserId: '',
      pendingChartFile: null,
      savedMarkerIcon: DEFAULT_MARKER_ICON,
      pendingMarkerIcon: PENDING_MARKER_ICON,
      _fsmDestroyed: false
    }
  },
  computed: {
    canAddMore () {
      if (this.disabled) return false
      const q = this.devicesQtyNumber
      if (q === null || q === undefined) return false
      return this.surveyWorks.length < q
    },
    mapMarkers () {
      const out = []
      this.surveyWorks.forEach((work, letterIndex) => {
        const position = parseSurveyWorkCoordinates(work.coordinates)
        if (position) out.push({ work, position, letterIndex })
      })
      return out
    },
    canSubmitPopup () {
      return (
        String(this.draftName || '').trim() !== '' &&
        String(this.draftUserId || '').trim() !== '' &&
        this.pending != null
      )
    },
    saveBtnColor () {
      if (this.saving) return '#0091d5'
      return this.canSubmitPopup ? '#0091d5' : '#e0e4e8'
    }
  },
  watch: {
    surveyWorks: {
      deep: true,
      handler () {
        this.$nextTick(() => this.fitMapToWorks())
      }
    }
  },
  mounted () {
    this.$gmapApiPromiseLazy().then(() => {
      this.initFieldWorkMarkerIcon()
      this.$nextTick(() => {
        if (this.$refs.gmap) this.$refs.gmap.resize()
        this.fitMapToWorks()
      })
    })
  },
  beforeDestroy () {
    this._fsmDestroyed = true
  },
  methods: {
    /**
     * Uses real PNG dimensions so labelOrigin sits on the badge center (Google defaults skew left).
     */
    initFieldWorkMarkerIcon () {
      const url = DEFAULT_MARKER_ICON
      const img = new Image()
      img.onload = () => {
        if (typeof window.google === 'undefined' || !window.google.maps) return
        const w = img.naturalWidth
        const h = img.naturalHeight
        if (!w || !h) return
        const g = window.google.maps
        // Pin tip at bottom center; label anchor on circular head (tweak fractions if art changes)
        const anchorX = Math.round(w / 2)
        const anchorY = h
        const labelX = Math.round(w / 2 + w * 0.035)
        const labelY = Math.round(h * 0.45)
        this.savedMarkerIcon = {
          url,
          scaledSize: new g.Size(w, h),
          anchor: new g.Point(anchorX, anchorY),
          labelOrigin: new g.Point(labelX, labelY)
        }
      }
      img.onerror = () => {
        this.savedMarkerIcon = url
      }
      img.src = url
    },
    markerLabel (letterIndex) {
      return {
        text: String.fromCharCode(65 + (letterIndex % 26)),
        color: 'white',
        fontSize: '16px',
        fontWeight: 'bold',
        fontFamily: 'Roboto, Arial, sans-serif'
      }
    },
    onMapClick (e) {
      if (this.disabled || this.saving) return
      if (!this.canAddMore) {
        this.$emit('add-blocked')
        return
      }
      const lat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat
      const lng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng
      this.pending = { lat, lng }
      this.draftName = ''
      this.draftUserId = ''
      this.pendingChartFile = null
      if (this.$refs.chartFileInput) this.$refs.chartFileInput.value = ''
    },
    closePending () {
      this.pending = null
      this.pendingChartFile = null
      if (this.$refs.chartFileInput) this.$refs.chartFileInput.value = ''
    },
    onPlaceChanged (place) {
      if (!place || !place.geometry || !place.geometry.location) return
      this.$refs.gmap.$mapPromise.then((map) => {
        const loc = place.geometry.location
        const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat
        const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng
        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport)
        } else {
          map.setCenter({ lat, lng })
          map.setZoom(14)
        }
      })
    },
    applyArgentinaEmptyView (map) {
      this.mapCenter = { ...ARGENTINA_CENTER }
      this.mapZoom = ARGENTINA_ZOOM
      map.setCenter(ARGENTINA_CENTER)
      map.setZoom(ARGENTINA_ZOOM)
    },
    tryGeolocateEmptyMap () {
      if (typeof navigator === 'undefined' || !navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== 'function') {
        return
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (this._fsmDestroyed) return
          if (this.mapMarkers.length) return
          const lat = pos.coords.latitude
          const lng = pos.coords.longitude
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
          this.mapCenter = { lat, lng }
          this.mapZoom = USER_LOCATION_ZOOM
          // Re-resolve map after async permission flow so we always target the live instance
          this.$nextTick(() => {
            if (this._fsmDestroyed || this.mapMarkers.length) return
            const g = this.$refs.gmap
            if (!g) return
            g.$mapPromise.then((liveMap) => {
              if (this._fsmDestroyed || this.mapMarkers.length) return
              liveMap.setCenter({ lat, lng })
              liveMap.setZoom(USER_LOCATION_ZOOM)
            })
          })
        },
        () => {
          /* Fallback already applied: Argentina + ARGENTINA_ZOOM */
        },
        GEOLOCATION_OPTIONS
      )
    },
    fitMapToWorks () {
      const list = this.mapMarkers
      if (!this.$refs.gmap) return
      this.$refs.gmap.$mapPromise.then((map) => {
        if (typeof window.google === 'undefined' || !window.google.maps) return
        if (!list.length) {
          this.applyArgentinaEmptyView(map)
          this.tryGeolocateEmptyMap()
          return
        }
        const bounds = new window.google.maps.LatLngBounds()
        list.forEach((m) => bounds.extend(m.position))
        map.fitBounds(bounds, 48)
      })
    },
    openChartFilePicker () {
      const el = this.$refs.chartFileInput
      if (el) el.click()
    },
    onChartFileSelected (e) {
      const input = e.target
      const f = input.files && input.files[0]
      if (input) input.value = ''
      this.pendingChartFile = f || null
    },
    submitPopup () {
      if (!this.canSubmitPopup || this.saving || !this.pending) return
      const coordinates = formatSurveyWorkCoordinates(this.pending.lat, this.pending.lng)
      const ponceTechnician = String(this.draftUserId) === PONCE_TECHNICIAN_SELECT_VALUE
      const userId = ponceTechnician ? '' : String(this.draftUserId).trim()
      this.$emit('create-work', {
        deviceName: String(this.draftName).trim(),
        userId,
        ponceTechnician,
        coordinates,
        pluviometricChartFile: this.pendingChartFile || null
      })
    },
    /** Called by parent after successful create to close popup */
    notifyCreateSuccess () {
      this.closePending()
    }
  }
}
</script>

<style scoped lang="stylus">
.fsm-root
  position absolute
  left 0
  right 0
  top 0
  bottom 0
  min-height 280px

.fsm-map
  width 100%
  height 100%

.fsm-search-bar
  position absolute
  top 12px
  left 12px
  right 12px
  max-width 360px
  z-index 2
  display flex
  align-items center
  background #fff
  border-radius 24px
  box-shadow 0 2px 8px rgba(0, 0, 0, 0.15)
  padding 4px 12px 4px 16px

.fsm-search-input
  flex 1
  border none
  outline none
  font-size 0.875rem
  line-height 1.4
  padding 8px 4px
  min-width 0

.fsm-search-icon
  flex-shrink 0

.fsm-popup
  min-width 220px
  max-width 300px
  font-family 'Source Sans 3', 'Segoe UI', system-ui, sans-serif
  box-sizing border-box
  background #fff
  border-radius 12px
  overflow hidden

.fsm-popup-body
  /* Extra top/right so content clears the Maps close control */
  padding 12px 12px

.fsm-popup-footer
  padding 12px 12px
  border-top 1px solid rgba(0, 32, 47, 0.06)
  background linear-gradient(180deg, rgba(250, 251, 252, 0.9) 0%, #fff 40%)

.fsm-field
  & + .fsm-field
    margin-top 12px

.fsm-field--upload
  margin-top 14px

.fsm-tech-select
  width 100%
  margin-top 6px

.fsm-label
  display block
  font-size 0.6875rem
  font-weight 600
  letter-spacing 0.06em
  text-transform uppercase
  color #90a4ae
  margin 0 0 6px

.fsm-input
  width 100%
  box-sizing border-box
  border 1px solid #e8ecef
  border-radius 10px
  padding 10px 12px
  font-size 0.875rem
  line-height 1.35
  color #263238
  background #fafbfc
  transition border-color 0.15s ease, background 0.15s ease

.fsm-input::placeholder
  color #b0bec5

.fsm-input:hover:not(:disabled)
  border-color #cfd8dc
  background #fff

.fsm-input:focus
  outline none
  border-color #0091d5
  background #fff
  box-shadow 0 0 0 3px rgba(0, 145, 213, 0.12)

.fsm-input:disabled
  opacity 0.65
  cursor not-allowed

.fsm-file-input
  position absolute
  width 1px
  height 1px
  opacity 0
  pointer-events none

.fsm-upload-zone
  display flex
  flex-direction column
  align-items center
  justify-content center
  gap 6px
  width 100%
  margin 0
  padding 14px 12px
  background transparent
  border 1px dashed #cfd8dc
  border-radius 10px
  cursor pointer
  font inherit
  color #546e7a
  transition border-color 0.15s ease, background 0.15s ease

.fsm-upload-zone:hover:not(:disabled)
  border-color #90a4ae
  background rgba(0, 145, 213, 0.04)

.fsm-upload-zone:disabled
  opacity 0.55
  cursor not-allowed

.fsm-upload-icon
  flex-shrink 0
  opacity 0.9

.fsm-upload-label
  font-size 0.8125rem
  font-weight 500
  line-height 1.3
  text-align center

.fsm-file-hint
  font-size 0.6875rem
  color #78909c
  margin 8px 0 0
  line-height 1.35
  word-break break-all
  text-align center

.fsm-save-btn
  font-weight 600
  font-size 0.9375rem
  letter-spacing 0.01em
  min-height 44px !important
  height 44px !important

/* White label without `dark` (custom hex + depressed) */
.fsm-save-btn:not(.v-btn--disabled),
.fsm-save-btn.v-btn--loading
  color #fff !important

.fsm-save-btn:not(.v-btn--disabled) >>> .v-btn__content,
.fsm-save-btn.v-btn--loading >>> .v-btn__content
  color #fff !important

/* Disabled (incomplete fields): muted grey */
.fsm-save-btn.v-btn--disabled:not(.v-btn--loading)
  background-color #e0e4e8 !important
  color #616161 !important
  opacity 1 !important

.fsm-save-btn.v-btn--disabled:not(.v-btn--loading)::before
  opacity 0 !important

/* Loading: keep primary fill + visible spinner */
.fsm-save-btn.v-btn--loading
  background-color #0091d5 !important
  color #fff !important
  opacity 1 !important

.fsm-save-btn.v-btn--loading::before
  opacity 0 !important

.fsm-save-btn.v-btn--loading >>> .v-btn__loader .v-progress-circular
  color #fff !important
</style>

<style lang="stylus">
/* Google Maps InfoWindow is rendered outside scoped CSS; target via parent class from FieldSurveyForm */
.fsf-map-embed .gm-style-iw-c
  border-radius 12px !important
  border none !important
  outline none !important
  box-shadow 0 4px 24px rgba(0, 32, 47, 0.12), 0 1px 3px rgba(0, 32, 47, 0.06) !important
  padding 8px !important
  box-sizing border-box !important

.fsf-map-embed .gm-style-iw-tc
  filter none

/* Close control: inset from card edge so it is not flush to the corner */
.fsf-map-embed button.gm-ui-hover-effect
  display flex !important
  align-items center !important
  justify-content center !important
  padding 0 !important
  width 34px !important
  height 34px !important
  opacity 0.72
  border-radius 10px !important
  background rgba(255, 255, 255, 0.92) !important
  box-shadow none !important
  -webkit-box-shadow none !important

.fsf-map-embed button.gm-ui-hover-effect span
  display flex !important
  align-items center !important
  justify-content center !important
  margin 0 !important
  line-height 0 !important

.fsf-map-embed button.gm-ui-hover-effect:hover
  opacity 0.95 !important
  background #fff !important
  box-shadow none !important
  -webkit-box-shadow none !important
</style>



// WEBPACK FOOTER //
// FieldSurveyMap.vue?5315d9ea